import React, { useEffect, useRef } from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Flatten react-hook-form's nested errors into [{ name, message }].
 */
export const flattenErrors = (errors, prefix = '') => {
  const list = [];
  if (!errors || typeof errors !== 'object') return list;
  Object.entries(errors).forEach(([key, value]) => {
    if (!value) return;
    const name = prefix ? `${prefix}.${key}` : key;
    if (typeof value.message === 'string' && value.message) {
      list.push({ name, message: value.message });
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (item) list.push(...flattenErrors(item, `${name}.${index}`));
      });
      return;
    }
    if (typeof value === 'object') {
      list.push(...flattenErrors(value, name));
    }
  });
  return list;
};

const findField = (name) => {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/"/g, '\\"');
  return document.querySelector(`[name="${escaped}"]`)
    || document.querySelector(`[data-field="${escaped}"]`);
};

// Order errors as the fields appear on the page; unknown fields go last.
const byPagePosition = (a, b) => {
  const elA = findField(a.name);
  const elB = findField(b.name);
  if (!elA || !elB) return (elA ? -1 : 0) + (elB ? 1 : 0);
  if (elA === elB) return 0;
  return elA.compareDocumentPosition(elB) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
};

const focusField = (name) => {
  const el = findField(name);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof el.focus === 'function') el.focus({ preventScroll: true });
  }
};

/**
 * Error box listing every failed field. Receives the form's `errors` object and
 * an optional `labels` map. Keys may be exact names ('personal.ssn') or use `*`
 * for array indexes ('employers.*.phone': 'Employer {n}: phone'), where {n} is
 * the 1-based index.
 *
 * `focusKey` should change once per failed submit (e.g. a counter). The box
 * takes focus only then: re-validation while the user types also changes the
 * error list, and stealing focus there swallows their keystrokes.
 */
const ErrorSummary = ({ errors, labels = {}, focusKey, title = 'Please fix the following before continuing', className = '' }) => {
  const ref = useRef(null);
  const pendingFocusRef = useRef(0);
  const items = flattenErrors(errors).sort(byPagePosition);
  const names = items.map((item) => item.name).join('|');

  // react-hook-form can publish the errors a render after the attempt counter
  // changes, so remember the request briefly and focus once errors are present.
  // The window keeps a stale request from firing later while the user types.
  useEffect(() => {
    if (focusKey) pendingFocusRef.current = Date.now();
  }, [focusKey]);

  useEffect(() => {
    if (Date.now() - pendingFocusRef.current < 1000 && items.length && ref.current) {
      pendingFocusRef.current = 0;
      ref.current.focus({ preventScroll: true });
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, names]);

  if (!items.length) return null;

  const labelFor = (name) => {
    if (labels[name]) return labels[name];
    const indexes = [];
    const pattern = name.replace(/\.(\d+)(?=\.|$)/g, (m, index) => { indexes.push(Number(index) + 1); return '.*'; });
    if (labels[pattern]) {
      let i = 0;
      return labels[pattern].replace(/\{n\}/g, () => indexes[Math.min(i++, indexes.length - 1)]);
    }
    const generic = name.replace(/\.\d+\./g, (m) => ` ${Number(m.replace(/\./g, '')) + 1} `).replace(/\./g, ' ');
    return generic.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
  };

  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="alert"
      aria-labelledby="error-summary-title"
      className={`scroll-mt-40 rounded-lg border border-red-200 bg-red-50 p-4 focus:outline-none focus:ring-2 focus:ring-red-400 ${className}`}
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h3 id="error-summary-title" className="text-sm font-semibold text-red-800">{title}</h3>
          <ul className="mt-2 space-y-1 text-sm text-red-700">
            {items.map((item) => (
              <li key={item.name}>
                <button
                  type="button"
                  onClick={() => focusField(item.name)}
                  className="text-left underline decoration-red-300 underline-offset-2 hover:decoration-red-600"
                >
                  <span className="font-medium">{labelFor(item.name)}:</span> {item.message}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ErrorSummary;
